import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, Users, Handshake, Mail, 
  LogOut, Menu, X, Plane, ChevronRight, Palette,
  UserCog, Settings, Shield, Image, Trophy, Youtube, Camera, FolderTree, FileCheck, Archive
} from 'lucide-react';
import { authAPI } from '../../services/adminApi';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSelector from '../../components/LanguageSelector';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [user, setUser] = useState(() => authAPI.getUser());
  const { t } = useLanguage();

  useEffect(() => {
    let mounted = true;
    // Refresh user info from API
    authAPI.getMe().then(data => {
      if (!mounted) return;
      setUser(data);
      localStorage.setItem('admin_user', JSON.stringify(data));
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  const handleLogout = () => {
    authAPI.logout();
    navigate('/admin/login');
  };

  const isAdmin = user?.role === 'admin';

  // Base menu items for all users
  const baseMenuItems = [
    { path: '/admin', icon: LayoutDashboard, labelKey: 'admin.sidebar.dashboard', exact: true },
    { path: '/admin/build-logs', icon: FileText, labelKey: 'admin.sidebar.buildLogs' },
    { path: '/admin/team', icon: Users, labelKey: 'admin.sidebar.team' },
    { path: '/admin/sponsors', icon: Handshake, labelKey: 'admin.sidebar.sponsors' },
    { path: '/admin/achievements', icon: Trophy, labelKey: 'admin.sidebar.achievements' },
    { path: '/admin/youtube', icon: Youtube, labelKey: 'admin.sidebar.youtube' },
    { path: '/admin/vehicle-gallery', icon: Camera, labelKey: 'admin.sidebar.vehicleGallery' },
    { path: '/admin/vehicle-categories', icon: FolderTree, labelKey: 'admin.sidebar.vehicleCategories' },
    { path: '/admin/competition-docs', icon: FileCheck, labelKey: 'admin.sidebar.competitionDocs' },
    { path: '/admin/messages', icon: Mail, labelKey: 'admin.sidebar.messages' },
    { path: '/admin/gallery', icon: Image, labelKey: 'admin.sidebar.gallery' },
  ];

  // Admin-only menu items
  const adminMenuItems = [
    { path: '/admin/content', icon: Palette, labelKey: 'admin.sidebar.content' },
    { path: '/admin/aircraft', icon: Plane, labelKey: 'admin.sidebar.aircraft' },
    { path: '/admin/users', icon: UserCog, labelKey: 'admin.sidebar.users' },
    { path: '/admin/backup', icon: Archive, labelKey: 'admin.sidebar.backup' },
  ];

  // Profile menu item for all users
  const profileMenuItem = { path: '/admin/profile', icon: Settings, labelKey: 'admin.sidebar.profile' };

  const menuItems = isAdmin 
    ? [...baseMenuItems, ...adminMenuItems, profileMenuItem]
    : [...baseMenuItems, profileMenuItem];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return (
        <span className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1" 
          style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#EF4444' }}>
          <Shield size={10} />
          Admin
        </span>
      );
    }
    return (
      <span className="text-xs px-2 py-0.5 rounded-full"
        style={{ background: 'rgba(0, 240, 255, 0.2)', color: '#00F0FF' }}>
        Editor
      </span>
    );
  };

  return (
    <div className="min-h-screen flex" style={{ background: '#0F172A' }}>
      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-50 flex flex-col h-screen transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-20'
        }`}
        style={{ 
          background: 'rgba(30, 41, 59, 0.95)',
          backdropFilter: 'blur(12px)',
          borderRight: '1px solid rgba(0, 240, 255, 0.2)'
        }}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 shrink-0" style={{
          borderBottom: '1px solid rgba(0, 240, 255, 0.2)'
        }}>
          {sidebarOpen && (
            <div className="flex items-center gap-3">
              <Plane size={28} style={{ color: '#00F0FF' }} />
              <span className="font-bold text-lg" style={{ color: '#F8FAFC' }}>Admin</span>
            </div>
          )}
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg transition-colors"
            style={{ color: '#94A3B8' }}
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Menu */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-2">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${
                isActive(item.path, item.exact) ? 'active' : ''
              }`}
              style={{
                background: isActive(item.path, item.exact) 
                  ? 'rgba(0, 240, 255, 0.15)' 
                  : 'transparent',
                color: isActive(item.path, item.exact) ? '#00F0FF' : '#94A3B8',
                border: isActive(item.path, item.exact) 
                  ? '1px solid rgba(0, 240, 255, 0.3)' 
                  : '1px solid transparent',
              }}
            >
              <item.icon size={20} />
              {sidebarOpen && <span>{t(item.labelKey)}</span>}
            </Link>
          ))}
        </nav>

        {/* User & Logout */}
        <div className="shrink-0 p-4" style={{
          borderTop: '1px solid rgba(0, 240, 255, 0.2)'
        }}>
          {sidebarOpen && user && (
            <div className="mb-3 px-4">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm font-medium" style={{ color: '#F8FAFC' }}>{user.name || user.email}</p>
                {getRoleBadge(user.role)}
              </div>
              <p className="text-xs" style={{ color: '#64748B' }}>{user.email}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-all duration-300"
            style={{ color: '#EF4444' }}
          >
            <LogOut size={20} />
            {sidebarOpen && <span>{t('admin.sidebar.logout')}</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Top Bar */}
        <header className="h-16 flex items-center justify-between px-6" style={{
          background: 'rgba(30, 41, 59, 0.6)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid rgba(0, 240, 255, 0.1)'
        }}>
          <div className="flex items-center gap-2 text-sm" style={{ color: '#94A3B8' }}>
            <Link to="/" className="hover:text-cyan-400 transition-colors">{t('nav.home')}</Link>
            <ChevronRight size={16} />
            <span style={{ color: '#F8FAFC' }}>Admin Panel</span>
          </div>
          <div className="flex items-center gap-4">
            {/* Language Selector */}
            <LanguageSelector />
            {user && (
              <div className="flex items-center gap-2">
                {getRoleBadge(user.role)}
              </div>
            )}
            <a 
              href="/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm px-4 py-2 rounded-lg transition-colors"
              style={{ 
                color: '#00F0FF',
                border: '1px solid rgba(0, 240, 255, 0.3)'
              }}
            >
              {t('nav.home')} →
            </a>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
